import * as tslib_1 from "tslib";
import * as React from "react";
import defaultTheme from ".";
/**
 * API:
 *
 * You can create custom themes using Object.assign:
 *
 *
 * Pass that theme to the theme provider. A theme should always
 * provide a dark and light color mode.
 *
 * <ThemeProvider theme={customTheme}><App /></ThemeProvider>
 *
 *
 * When you want to use a dark mode:
 *
 * function App () {
 *  return <Dark><SomeContent /></Dark>
 * }
 *
 *
 * To consume a theme, use `useTheme` hook
 *
 * const theme = useTheme()
 * // theme.colors.text.default (will be dark or light depending on the mode)
 * // theme.colors.mode === 'dark' or 'light'
 */
var ThemeContext = React.createContext(defaultTheme);
export var ThemeProvider = function (_a) {
    var _b = _a.theme, theme = _b === void 0 ? defaultTheme : _b, children = _a.children;
    return (React.createElement(ThemeContext.Provider, { value: theme }, children));
};
/**
 * A hook for consuming the theme
 */
export function useTheme() {
    return React.useContext(ThemeContext);
}
var ColorMode = React.forwardRef(function (_a, ref) {
    var colors = _a.colors, children = _a.children, other = tslib_1.__rest(_a, ["colors", "children"]);
    var theme = useTheme();
    // memo is necessary to prevent unnecessary rerenders
    // https://reactjs.org/docs/context.html#caveats
    var adjustedTheme = React.useMemo(function () { return mergeColors(theme, colors); }, [
        theme,
        colors
    ]);
    return (React.createElement(ThemeContext.Provider, { value: adjustedTheme }, typeof children === "function"
        ? children(adjustedTheme)
        : React.cloneElement(React.Children.only(children), tslib_1.__assign({ ref: ref }, other))));
});
ColorMode.displayName = "ColorMode";
function mergeColors(theme, colors) {
    return tslib_1.__assign({}, theme, { shadows: colors.shadows, colors: colors });
}
export var LightMode = React.forwardRef(function (_a, ref) {
    var children = _a.children, other = tslib_1.__rest(_a, ["children"]);
    var theme = useTheme();
    return (React.createElement(ColorMode, tslib_1.__assign({ colors: theme.modes.light, ref: ref }, other), children));
});
LightMode.displayName = "LightMode";
/**
 * Provide a dark theme
 */
export var DarkMode = React.forwardRef(function (_a, ref) {
    var children = _a.children, other = tslib_1.__rest(_a, ["children"]);
    var theme = useTheme();
    return (React.createElement(ColorMode, tslib_1.__assign({ colors: theme.modes.dark, ref: ref }, other), children));
});
DarkMode.displayName = "DarkMode";
